/** Error carrying the backend's `detail` message and HTTP status. */
export class ApiError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

/** Throws an `ApiError` with the API's `detail` (FastAPI error shape) when the response failed.
 * Returns the response untouched otherwise, so `await ensureOk(res)` can precede `res.json()`. */
export async function ensureOk(
  response: Response,
  fallback = "The request failed.",
): Promise<Response> {
  if (response.ok) return response;

  let detail = fallback;
  try {
    const body = (await response.clone().json()) as { detail?: unknown };
    if (typeof body.detail === "string" && body.detail.trim()) {
      detail = body.detail;
    } else if (Array.isArray(body.detail)) {
      // Pydantic 422 validation errors: [{ loc, msg, type }, ...]
      const messages = body.detail
        .map((item) => (item as { msg?: string }).msg)
        .filter((msg): msg is string => Boolean(msg));
      if (messages.length) detail = messages.join(" — ");
    }
  } catch {
    // Non-JSON error body — keep the fallback message.
  }

  throw new ApiError(detail, response.status);
}

/** Human-readable message for any thrown error (ApiError detail, Error message, or a fallback). */
export function errorMessage(
  error: unknown,
  fallback = "Something went wrong.",
): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}
